import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, ShieldAlert } from "lucide-react";

type AuditEvent = {
  id: string;
  referral_id: string | null;
  event_type: string;
  actor_id: string | null;
  actor_kind: string;
  referrer_id: string | null;
  referred_user_id: string | null;
  code: string | null;
  status_before: string | null;
  status_after: string | null;
  review_status_before: string | null;
  review_status_after: string | null;
  risk_score: number | null;
  risk_flags: string[] | null;
  hold_reason: string | null;
  amount_cents: number | null;
  detail: Record<string, unknown> | null;
  created_at: string;
};

const EVENT_TYPES = [
  "all",
  "referral_claimed",
  "fraud_hold",
  "fraud_signal",
  "admin_override",
  "referral_completed",
  "referral_voided",
  "referral_updated",
] as const;

const TONE: Record<string, string> = {
  fraud_hold: "border-destructive/40 text-destructive",
  fraud_signal: "border-destructive/40 text-destructive",
  admin_override: "border-primary/40 text-primary",
  referral_completed: "border-primary/40 text-primary",
};

/** Admin investigation view over the referral audit trail. */
export function ReferralAuditLogPanel() {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<string>("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase
      .from("referral_audit_events")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (type !== "all") q = q.eq("event_type", type);
    const { data, error } = await q;
    if (error) toast.error(error.message);
    setRows((data as unknown as AuditEvent[]) ?? []);
    setLoading(false);
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const s = query.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [
        r.referral_id,
        r.actor_id,
        r.referrer_id,
        r.referred_user_id,
        r.code,
        r.hold_reason,
        JSON.stringify(r.detail ?? {}),
      ]
        .join(" ")
        .toLowerCase()
        .includes(s),
    );
  }, [rows, query]);

  const exportCsv = () => {
    const cols: (keyof AuditEvent)[] = [
      "created_at",
      "event_type",
      "actor_kind",
      "actor_id",
      "referral_id",
      "referrer_id",
      "referred_user_id",
      "code",
      "status_before",
      "status_after",
      "review_status_before",
      "review_status_after",
      "risk_score",
      "hold_reason",
      "amount_cents",
    ];
    const cell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [
      cols.join(","),
      ...filtered.map((r) => cols.map((c) => cell(r[c])).join(",")),
      ...[],
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `referral-audit-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <ShieldAlert className="size-5 text-primary" />
          Referral audit trail
        </h2>
        <div className="flex flex-wrap gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-8 w-44 font-mono text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t.replace(/_/g, " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by id, code, reason…"
            className="h-8 w-60 font-mono text-xs"
          />
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>
            {loading ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
            <Download className="size-3 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" /> Loading audit trail…
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No referral audit events match this view.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} className="rounded-lg border border-border/60 bg-secondary/20 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border ${
                    TONE[r.event_type] ?? "border-muted-foreground/40 text-muted-foreground"
                  }`}
                >
                  {r.event_type.replace(/_/g, " ")}
                </span>
                <span className="font-mono text-[10px] uppercase text-muted-foreground">
                  {r.actor_kind}
                  {r.actor_id ? ` ${r.actor_id.slice(0, 8)}…` : ""}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </span>
                {typeof r.risk_score === "number" && (
                  <span className="font-mono text-[10px] uppercase text-muted-foreground">
                    risk {r.risk_score}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs">
                {r.status_before || r.status_after ? (
                  <span className="font-mono">
                    {r.status_before ?? "—"} → {r.status_after ?? "—"}
                  </span>
                ) : null}
                {r.review_status_after && (
                  <span className="font-mono text-muted-foreground">
                    {" "}
                    · review {r.review_status_before ?? "—"} → {r.review_status_after}
                  </span>
                )}
              </div>
              {r.hold_reason && (
                <div className="text-xs text-muted-foreground mt-1">
                  <b>Hold reason:</b> {r.hold_reason}
                </div>
              )}
              {r.risk_flags && r.risk_flags.length > 0 && (
                <div className="font-mono text-[10px] text-muted-foreground mt-1">
                  flags: {r.risk_flags.join(", ")}
                </div>
              )}
              <div className="font-mono text-[10px] text-muted-foreground mt-1 break-all">
                referral {r.referral_id?.slice(0, 8) ?? "—"}… · referrer{" "}
                {r.referrer_id?.slice(0, 8) ?? "—"}… · friend{" "}
                {r.referred_user_id?.slice(0, 8) ?? "—"}…
                {typeof r.amount_cents === "number"
                  ? ` · $${(r.amount_cents / 100).toFixed(2)}`
                  : ""}
              </div>
              {r.detail && Object.keys(r.detail).length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer font-mono text-[10px] uppercase text-muted-foreground">
                    detail
                  </summary>
                  <pre className="mt-1 overflow-x-auto rounded bg-background/60 p-2 text-[10px]">
                    {JSON.stringify(r.detail, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
